import React, {useMemo, useState} from 'react';
import {useTheme} from '@mui/material/styles';
import {useQuery, useQueryClient} from 'react-query';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {DataGridPro} from '@mui/x-data-grid-pro';
import Canvas from '../../navigation/canvas/Canvas';
import ErrorPopup from '../../common/ErrorPopup';
import MessagePopup from '../../common/MessagePopup';
import IyoAuth from '../../../auth/Auth';
import ProviderThresholdsService from '../../../apis/ProviderThresholdsService';
import ProviderCategoryService from '../../../apis/ProviderCategoryService';
import ProviderService from '../../../apis/ProviderService';
import {buildColumns} from './columns';

const DEFAULT_THRESHOLDS = {low_threshold: 24, med_threshold: 48, high_threshold: 72};

const ProviderThresholds = ({}) => {
  const theme = useTheme();
  // ####################[ Setup and Initialization ]####################
  const queryClient = useQueryClient();

  // ####################[ State Definitions ]####################
  const [rows, setRows] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [saving, setSaving] = useState(false);
  const [errDisplayText, setErrDisplayText] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState('');

  // ####################[ Event Handlers ]####################
  const handleError = (error) => {
    setErrorOpen(false);
    setErrDisplayText(`${error.message}` +
      `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`);
    setErrorOpen(true);
    console.error(`${error.message}: ${error.response ? JSON.stringify(error.response.data) : ''}`);
  };

  // ####################[ Query Definitions ]####################
  const getCategories = useQuery('getProviderCategories', async () => {
      return await ProviderCategoryService.getAll();
    },
    {
      onError: (error) => handleError(error),
      retry: false,
      staleTime: Infinity
    }
  );

  const getProviders = useQuery('getProvidersForThresholds', async () => {
      return await ProviderService.getAll();
    },
    {
      onError: (error) => handleError(error),
      retry: false,
      staleTime: Infinity
    }
  );

  const getThresholds = useQuery('getProviderThresholdsPage', async () => {
      const data = await ProviderThresholdsService.getAll();
      const normalized = (data || []).map((row) => ({
        id: row.id,
        provider_id: row.provider_id,
        provider: row.provider_name || '',
        provider_category_id: row.category_id,
        category_name: row.category_name,
        low_threshold: row.low_threshold,
        med_threshold: row.med_threshold,
        high_threshold: row.high_threshold,
        alias: row.alias,
        description: row.description
      }));
      setRows(normalized);
      setDeletedIds([]);
      setDirty(false);
      return normalized;
    },
    {
      onError: (error) => handleError(error),
      retry: false,
      refetchOnMount: 'always'
    }
  );

  // ####################[ Derived Data ]####################
  const categoryOptions = useMemo(() => {
    return (getCategories.data || [])
      .filter((c) => c.active)
      .map((c) => ({value: c.id, label: c.name}))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, {sensitivity: 'base'}));
  }, [getCategories.data]);

  const availableProviders = useMemo(() => {
    const usedIds = new Set(rows.map((r) => r.provider_id).filter(Boolean));
    return (getProviders.data || [])
      .filter((p) => !usedIds.has(p.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'}));
  }, [rows, getProviders.data]);

  const handleAdd = () => {
    if (!selectedProviderId) return;
    const provider = (getProviders.data || []).find((p) => p.id === selectedProviderId);
    if (!provider) return;

    const newRow = {
      id: `new-${Date.now()}`,
      isNew: true,
      provider: provider.name,
      provider_id: provider.id,
      provider_category_id: '',
      category_name: '',
      alias: provider.name.replace(/_/g, ' '),
      description: null,
      ...DEFAULT_THRESHOLDS
    };
    setRows((prev) => [...prev, newRow]);
    setSelectedProviderId('');
    setDirty(true);
  };

  const handleDelete = (row) => {
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    if (!row.isNew) setDeletedIds((prev) => [...prev, row.id]);
    setDirty(true);
  };

  const processRowUpdate = (newRow) => {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
    setDirty(true);
    return newRow;
  };

  const handleCancel = async () => {
    setDeletedIds([]);
    await queryClient.invalidateQueries('getProviderThresholdsPage');
  };

  const handleSave = async () => {
    const missingCategory = rows.filter((r) => !r.provider_category_id);
    if (missingCategory.length > 0) {
      const names = missingCategory.map((r) => r.provider || `row ${r.id}`).join(', ');
      setErrDisplayText(`Category is required for: ${names}`);
      setErrorOpen(true);
      return;
    }

    setSaving(true);
    try {
      const user = await IyoAuth.getUser();
      const original = getThresholds.data || [];
      const originalById = new Map(original.map((r) => [r.id, r]));

      const deletes = deletedIds.map((id) => ProviderThresholdsService.remove(id));

      const upserts = rows.map((row) => {
        const payload = {
          provider_id: row.provider_id,
          provider_category_id: Number(row.provider_category_id) || null,
          alias: row.alias || null,
          low_threshold: row.low_threshold === '' || row.low_threshold == null ? null : Number(row.low_threshold),
          med_threshold: row.med_threshold === '' || row.med_threshold == null ? null : Number(row.med_threshold),
          high_threshold: row.high_threshold === '' || row.high_threshold == null ? null : Number(row.high_threshold),
          description: row.description || null,
          updated_by: user
        };
        if (row.isNew) {
          return ProviderThresholdsService.post({...payload, created_by: user});
        }
        const prev = originalById.get(row.id);
        const changed = !prev
          || prev.provider_id !== payload.provider_id
          || prev.low_threshold !== payload.low_threshold
          || prev.med_threshold !== payload.med_threshold
          || prev.high_threshold !== payload.high_threshold
          || prev.provider_category_id !== payload.provider_category_id
          || prev.alias !== payload.alias
          || prev.description !== payload.description;
        return changed
          ? ProviderThresholdsService.put(row.id, {id: row.id, ...payload})
          : null;
      }).filter(Boolean);

      await Promise.all([...deletes, ...upserts]);

      setMsgText('Provider thresholds saved');
      setMsgOpen(true);
      await queryClient.invalidateQueries('getProviderThresholdsPage');
    } catch (error) {
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  // ####################[ Columns with Action ]####################
  const cols = useMemo(() => buildColumns(categoryOptions).concat({
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Tooltip title='Delete'>
        <IconButton color='primary' onClick={() => handleDelete(params.row)}>
          <DeleteIcon/>
        </IconButton>
      </Tooltip>
    )
  }), [categoryOptions]);

  const loading = getThresholds.isLoading || getProviders.isLoading || getCategories.isLoading;

  // ####################[ The Component ]####################
  return (
    <Box>
      <Canvas appName='nGEST' title='Provider Thresholds'>
        <Box sx={{mb: '1rem', display: 'flex', alignItems: 'center', gap: '1rem'}}>
          <FormControl size='small' sx={{minWidth: 240}}>
            <InputLabel id='provider-filter-label'>Provider</InputLabel>
            <Select
              labelId='provider-filter-label'
              label='Provider'
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
            >
              {availableProviders.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            color='primary'
            variant='contained'
            onClick={handleAdd}
            disabled={!selectedProviderId}
          >
            Add
          </Button>
        </Box>

        <Paper sx={{height: '65vh'}}>
          <DataGridPro
            columns={cols}
            rows={rows}
            getRowId={(row) => row.id}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleError}
            slots={{loadingOverlay: LinearProgress}}
            loading={loading}
            density='compact'
            disableRowSelectionOnClick
            sx={{
              '& .category-missing': {
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                color: theme.palette.error.main,
                fontStyle: 'italic'
              }
            }}
          />
        </Paper>

        <Box sx={{mt: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
          <Button
            color='primary'
            variant='outlined'
            onClick={handleCancel}
            disabled={saving || !dirty}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            Save
          </Button>
        </Box>
      </Canvas>

      <MessagePopup
        open={msgOpen}
        setOpen={setMsgOpen}
        displayText={msgText}
      />
      <ErrorPopup
        open={errorOpen}
        setOpen={setErrorOpen}
        displayText={errDisplayText}
      />
    </Box>
  );
};

export default ProviderThresholds;
